
import React, { useRef } from "react";
import { Upload, Image } from "lucide-react";
import { Button } from "@/components/ui/button";

interface LogoUploadProps {
  logoPreview: string | null;
  onLogoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveLogo: () => void;
}

const LogoUpload = ({ logoPreview, onLogoChange, onRemoveLogo }: LogoUploadProps) => {
  // Create a reference to the file input element
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Handle button click to trigger the file input
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      // Reset the value to ensure the change event fires even if the same file is selected
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Company Logo</h3>
      <p className="text-sm text-redflags-gray">
        Your logo will appear on your company profile and in search results.
        <span className="font-medium block mt-1">Recommended: 400 × 400 pixels</span>
      </p>
      
      <div className="flex items-center gap-4">
        <div className="border rounded-lg p-4 w-32 h-32 flex items-center justify-center bg-white">
          {logoPreview ? (
            <img 
              src={logoPreview} 
              alt="Company logo" 
              className="max-w-full max-h-full object-contain"
              key={logoPreview} // Add key to force re-render when image changes
            />
          ) : (
            <Image className="w-12 h-12 text-gray-300" />
          )}
        </div>
        
        <div className="flex flex-col space-y-2">
          <Button 
            variant="outline" 
            type="button" 
            className="flex items-center gap-2"
            onClick={handleButtonClick}
          >
            <Upload className="w-4 h-4" />
            {logoPreview ? 'Change logo' : 'Upload logo'}
          </Button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onLogoChange}
          />
          
          {logoPreview && (
            <Button 
              variant="outline" 
              type="button" 
              onClick={onRemoveLogo}
              className="text-redflags-red"
            >
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogoUpload;
